import React, { useMemo, useState } from "react";
import propTypes from "prop-types";
import _ from "lodash";
import {
  Backdrop,
  CircularProgress,
  Typography,
  Stack,
  Divider,
  Button,
} from "@mui/material";
import { Form, Formik } from "formik";
import MovementName from "./MovementName";
import MovementId from "./MovementId";
import ManualMovement, {
  TYPE as MMType,
  defaultValue as MMDefaultValue,
  schema as MMschema,
} from "./manual-movements/ManualMovement";
import { defaultValue as MNdefault, schema as MNschema } from "./MovementName";
import { defaultValue as MIdefault, schema as MIschema } from "./MovementId";
import * as Yup from "yup";
import { useSensorsStreamListener } from "../../sensors/sensors-stream";
import { useOnlineDeviceSensors } from "../../live/online-device-provider";
import { defaultValue as SIdefault } from "./sensor-inputs/SensorInput";
import { defaultValue as MMPhaseDefault } from "./manual-movements/ManualMovementPhase";
import { useTranslation } from "react-i18next";

export const schema = Yup.object({
  i: MIschema,
  n: MNschema,
  t: Yup.string().oneOf([MMType], `Movement type must be in [${MMType}].`),
  c: Yup.object().when("t", (t, schema) => {
    if (t === MMType) return MMschema;
    else return schema;
  }),
});

export const TYPE = "mm";
export const defaultValue = {
  i: MIdefault,
  n: MNdefault,
  t: MMType,
  c: MMDefaultValue,
};

const DEFAULT_VALUES_MAP = {
  [MMType]: MMDefaultValue,
};

export const useDefaultValue = (t, movement) => {
  const sensors = useOnlineDeviceSensors();
  return useMemo(() => {
    if (movement === undefined) {
      const c = _.cloneDeep(DEFAULT_VALUES_MAP[t] || {});
      if (t === MMType && sensors.length > 0) {
        const phase = {
          ..._.cloneDeep(MMPhaseDefault),
          n: "Phase 1",
          i: sensors.map((s) => ({ ...SIdefault, n: s.k })),
        };
        c.p = [phase];
      }
      return { ...defaultValue, t, c };
    } else {
      return movement;
    }
  }, [sensors]);
};

function LoadingBackdrop({}) {
  return (
    <Backdrop
      sx={{ position: "absolute", backgroundColor: "rgba(0, 0, 0, 0.1)" }}
      open
    >
      <CircularProgress />
    </Backdrop>
  );
}

LoadingBackdrop.propTypes = {};

function MovementTypeName({ type }) {
  const { t } = useTranslation();

  switch (type) {
    case MMType:
      return <Typography>{t("MOVE_CONF.MANUAL_MOVEMENT")}</Typography>;
    default:
      return null;
  }
}

MovementTypeName.propTypes = {
  type: propTypes.string,
};

function MovementTypeSwitch({ type, ...props }) {
  switch (type) {
    case MMType:
      return <ManualMovement {...props} />;
    default:
      return null;
  }
}

MovementTypeSwitch.propTypes = {
  type: propTypes.string,
};

export default function Movement({
  onSubmit = async () => {},
  movement,
  ...props
}) {
  const { t } = useTranslation();
  const [type, setType] = useState(MMType);
  const defaultValue = useDefaultValue(type, movement);
  useSensorsStreamListener();

  return (
    <Formik
      validationSchema={schema}
      initialValues={defaultValue}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await onSubmit(values);
        } catch (e) {
          console.error(e);
        }
        setSubmitting(false);
      }}
    >
      {({ values, isSubmitting, errors }) => {
        return (
          <Form>
            <Stack alignItems="center" spacing={4}>
              <Stack spacing={2} direction="column" alignItems="center">
                <MovementTypeName type={values.t} />
                <MovementName namespace="n" />
                <MovementId namespace="i" />
              </Stack>
              <MovementTypeSwitch {...props} namespace="c" type={values.t} />
              <Button disabled={Object.keys(errors).length !== 0} type="submit">
                {t("MOVE_CONF.SAVE_BUTTON")}
              </Button>
            </Stack>
            {isSubmitting ? <LoadingBackdrop /> : null}
          </Form>
        );
      }}
    </Formik>
  );
}

Movement.propTypes = {
  onSubmit: propTypes.func,
  movement: propTypes.object,
  sensors: propTypes.array,
  reactions: propTypes.array,
};
